import React, {useEffect, useRef, useState} from 'react'
import ApexCharts, {ApexOptions} from 'apexcharts'
import {getOSBreakdownApi} from '../../../modules/Dashboard/DashboardCURD'

interface OSBreakdownItem {
  osName: string
  totalCount: number
  percentage: number
}

interface Resp {
  isSuccess: boolean
  message: string
  data: OSBreakdownItem[]
}

interface IState {
  loading: boolean
  items: OSBreakdownItem[]
  error: string
}

const COLORS = ['#16a34a', '#111827', '#2563eb']

function formatIndian(v: number): string {
  if (v >= 100000) return `${(v / 100000).toFixed(2)}L`
  return v.toLocaleString('en-IN')
}

async function fetchOSBreakdown(memberId = 0): Promise<Resp> {
  const response = await getOSBreakdownApi(memberId)
  return response.data
}

function buildOptions(labels: string[], series: number[], colors: string[]): ApexOptions {
  return {
    chart: {
      type: 'donut',
      height: 220,
      toolbar: {show: false},
      fontFamily: 'inherit',
    },
    labels,
    series,
    colors,
    legend: {show: false},
    dataLabels: {enabled: false},
    stroke: {width: 0},
    plotOptions: {
      pie: {
        donut: {
          size: '68%',
          labels: {
            show: true,
            total: {
              show: true,
              label: 'OS',
            },
          },
        },
      },
    },
  }
}

const getOSIcon = (name: string) => {
  const n = name.toLowerCase()

  if (n.includes('android')) return 'bi bi-android2'
  if (n.includes('ios')) return 'bi bi-apple'

  return 'bi bi-cpu'
}

const OSBreakdownWidget: React.FC<{memberId?: number; className?: string}> = ({
  memberId = 0,
  className = '',
}) => {
  const chartRef = useRef<HTMLDivElement | null>(null)
  const chartInstance = useRef<ApexCharts | null>(null)

  const [state, setState] = useState<IState>({
    loading: true,
    items: [],
    error: '',
  })

  useEffect(() => {
    fetchOSBreakdown(memberId)
      .then((resp) => {
        if (resp.isSuccess) {
          setState({
            loading: false,
            items: resp.data || [],
            error: '',
          })
        } else {
          setState({
            loading: false,
            items: [],
            error: resp.message,
          })
        }
      })
      .catch((err) => {
        setState({
          loading: false,
          items: [],
          error: String(err),
        })
      })
  }, [memberId])

  useEffect(() => {
    if (state.loading || !chartRef.current || state.items.length === 0) return

    const labels = state.items.map((x) => x.osName)
    const series = state.items.map((x) => Number(x.totalCount))
    const colors = state.items.map((_, i) => COLORS[i % COLORS.length])

    if (chartInstance.current) {
      chartInstance.current.updateOptions(buildOptions(labels, series, colors))
    } else {
      chartInstance.current = new ApexCharts(chartRef.current, buildOptions(labels, series, colors))

      chartInstance.current.render()
    }

    return () => {
      chartInstance.current?.destroy()
      chartInstance.current = null
    }
  }, [state.loading, state.items])

  return (
    <div className={`${className}`}>
      <h5 className='fw-bold mb-3 text-success'>
        <i className='bi bi-phone me-2' />
        OS Breakdown
      </h5>

      <div className='card-body p-4'>
        {state.loading ? (
          <div className='text-center py-5'>Loading...</div>
        ) : state.error ? (
          <div className='text-danger text-center py-5'>{state.error}</div>
        ) : (
          <>
            <div ref={chartRef} />

            <div className='d-flex flex-column gap-2 mt-3'>
              {state.items.map((item, idx) => {
                const color = COLORS[idx % COLORS.length]

                return (
                  <div
                    key={idx}
                    className='d-flex align-items-center gap-2 px-3 py-2 rounded-3'
                    style={{
                      background: '#f9fafb',
                      border: '1px solid #f3f4f6',
                    }}
                  >
                    <span
                      className='rounded-circle'
                      style={{
                        width: 10,
                        height: 10,
                        background: color,
                      }}
                    />

                    <i
                      className={getOSIcon(item.osName)}
                      style={{
                        color,
                        fontSize: 18,
                      }}
                    />

                    <span className='flex-fill fw-medium small'>{item.osName}</span>

                    <span className='fw-bold small'>{formatIndian(Number(item.totalCount))}</span>

                    <span
                      className='rounded-pill fw-bold'
                      style={{
                        background: color + '1a',
                        color,
                        padding: '2px 8px',
                        fontSize: 11,
                      }}
                    >
                      {Number(item.percentage).toFixed(1)}%
                    </span>
                  </div>
                )
              })}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export {OSBreakdownWidget}
